import React, { useState } from "react";
import { api, ApiError } from "../../api";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";

export interface BillableRoom {
  roomId: string;
  roomNumber: number | null;
  sharingType: string;
  count: number;
}

function lastMonths(n: number): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

function monthName(m: string) {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

// Bottom sheet for adding an electricity bill. Pass `rooms` to bill a PG room
// (split between its tenants) or `tenancyId` to bill one tenant directly.
export function ElectricityForm({
  rooms,
  tenancyId,
  onClose,
  onDone,
}: {
  rooms?: BillableRoom[];
  tenancyId?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useLanguage();
  const toast = useToast();
  const months = lastMonths(3);
  const [roomId, setRoomId] = useState(rooms?.[0]?.roomId ?? "");
  const [month, setMonth] = useState(months[0]);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    const total = Number(amount);
    if (!Number.isInteger(total) || total < 1 || (!tenancyId && !roomId)) return setError(t("ownerLedgers.electricityInvalid"));
    setBusy(true);
    setError("");
    try {
      const body: Record<string, unknown> = { month, totalAmount: total };
      if (tenancyId) body.tenancyId = tenancyId;
      else body.roomId = roomId;
      if (note.trim()) body.note = note.trim();
      const res = await api.post("/api/electricity", body);
      toast(
        res.skipped.length
          ? `${t("ownerLedgers.electricityAdded", { n: res.applied.length })} · ${t("ownerLedgers.electricitySkipped", { n: res.skipped.length })}`
          : t("ownerLedgers.electricityAdded", { n: res.applied.length })
      );
      onDone();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="filter-backdrop" onClick={onClose} />
      <div className="filter-sheet">
        <div className="filter-sheet-handle" />
        <div className="filter-sheet-head">
          <div className="filter-sheet-title">{t("ownerLedgers.electricityTitle")}</div>
          <div className="filter-sheet-close" onClick={onClose}>✕</div>
        </div>
        {error && <div className="error-banner">{error}</div>}

        {rooms && (
          <div className="field">
            <label>{t("ownerLedgers.electricityRoomLabel")}</label>
            <div className="input-wrap">
              <select value={roomId} onChange={(e) => setRoomId(e.target.value)}>
                {rooms.map((r) => (
                  <option key={r.roomId} value={r.roomId}>
                    {t("ownerLedgers.electricityRoomOption", { n: r.roomNumber ?? "", type: r.sharingType, count: r.count })}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="field">
          <label>{t("ownerLedgers.electricityMonthLabel")}</label>
          <div className="input-wrap">
            <select value={month} onChange={(e) => setMonth(e.target.value)}>
              {months.map((m) => (
                <option key={m} value={m}>
                  {monthName(m)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <label>{t("ownerLedgers.electricityAmountLabel")}</label>
          <div className="input-wrap">
            <input inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, "").slice(0, 7))} placeholder="0" />
          </div>
          {rooms && <div className="field-hint">{t("ownerLedgers.electricityAmountSplitHint")}</div>}
        </div>

        <div className="field">
          <label>{t("ownerLedgers.electricityNoteLabel")}</label>
          <div className="input-wrap">
            <input value={note} maxLength={60} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        <button className="btn-solid" disabled={busy} onClick={submit}>
          {t("ownerLedgers.electricitySubmit")}
        </button>
      </div>
    </>
  );
}
